import type { Fetcher } from "./local-transport.ts";
import { OrniTransportError } from "./transport.ts";

export interface CloudHttpOptions {
  cloudUrl: string;
  accessToken?: string | (() => Promise<string | null>);
  recoverUnauthorized?: () => Promise<boolean>;
  requestTimeoutMs?: number;
  fetcher?: Fetcher;
  client?: CloudClientMetadata;
}

export interface CloudRequestOptions {
  /** Override the short control-plane timeout for a bounded large upload. */
  timeoutMs?: number;
}

export interface CloudClientMetadata {
  name: "desktop" | "web" | "ios" | "android" | "daemon" | "api-daemon" | "cli";
  version: string;
  protocolVersion: number;
}

export class CloudHttpClient {
  private readonly baseUrl: string;
  private readonly accessToken?: CloudHttpOptions["accessToken"];
  private readonly recoverUnauthorized?: CloudHttpOptions["recoverUnauthorized"];
  private readonly requestTimeoutMs: number;
  private readonly fetcher: Fetcher;
  private readonly client?: CloudClientMetadata;

  constructor(options: CloudHttpOptions) {
    this.baseUrl = options.cloudUrl.replace(/\/$/, "");
    this.accessToken = options.accessToken;
    this.recoverUnauthorized = options.recoverUnauthorized;
    this.requestTimeoutMs = options.requestTimeoutMs ?? 12_000;
    this.fetcher = options.fetcher ?? globalThis.fetch.bind(globalThis);
    this.client = options.client;
  }

  async request(
    path: string,
    init: RequestInit = {},
    options: CloudRequestOptions = {},
  ): Promise<unknown> {
    if (!path.startsWith("/")) {
      throw new OrniTransportError("INVALID_ARGUMENT", "Cloud request paths must be absolute.");
    }
    this.assertOnline();
    try {
      const timeoutMs = Math.max(1, options.timeoutMs ?? this.requestTimeoutMs);
      let response = await this.send(path, init, timeoutMs);
      if (response.status === 401 && await this.tryRecoverUnauthorized()) {
        response = await this.send(path, init, timeoutMs);
      }
      if (!response.ok) throw await cloudHttpError(response);
      return response.status === 204 ? {} : response.json();
    } catch (cause) {
      if (cause instanceof OrniTransportError) throw cause;
      if (cause instanceof Error && cause.name === "AbortError") {
        throw new OrniTransportError("TIMEOUT", "The Cloud request timed out.", true);
      }
      throw new OrniTransportError("OFFLINE", "Orni Cloud is unavailable.", true);
    }
  }

  /** Open a long-lived authenticated response such as the Cloud event stream. */
  async openStream(path: string, signal?: AbortSignal): Promise<Response> {
    if (!path.startsWith("/")) {
      throw new OrniTransportError("INVALID_ARGUMENT", "Cloud request paths must be absolute.");
    }
    this.assertOnline();
    try {
      let response = await this.sendStream(path, signal);
      if (response.status === 401 && await this.tryRecoverUnauthorized()) {
        response = await this.sendStream(path, signal);
      }
      if (!response.ok) throw await cloudHttpError(response);
      if (!response.body) {
        throw new OrniTransportError("UNAVAILABLE", "Cloud event stream has no response body.", true);
      }
      return response;
    } catch (cause) {
      if (cause instanceof OrniTransportError) throw cause;
      if (cause instanceof Error && cause.name === "AbortError") {
        throw new OrniTransportError("TIMEOUT", "The Cloud event stream was cancelled.", true);
      }
      throw new OrniTransportError("OFFLINE", "Orni Cloud event stream is unavailable.", true);
    }
  }

  private assertOnline(): void {
    // Browsers expose an authoritative local connectivity hint. Fail fast
    // while explicitly offline instead of waiting for a 12–90s command
    // deadline; callers keep their last snapshot and can retry on reconnect.
    // Do not require `navigator` so native/Node transports retain their own
    // reachability checks.
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      throw new OrniTransportError("OFFLINE", "Orni Cloud is offline.", true);
    }
  }

  private async send(path: string, init: RequestInit, timeoutMs = this.requestTimeoutMs): Promise<Response> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const token = await this.resolveAccessToken();
      return await this.fetcher(`${this.baseUrl}${path}`, {
        ...init,
        credentials: "include",
        signal: controller.signal,
        headers: requestHeaders(init.headers, token, this.client, Boolean(init.body)),
      });
    } finally {
      clearTimeout(timer);
    }
  }

  private async sendStream(path: string, signal?: AbortSignal): Promise<Response> {
    const token = await this.resolveAccessToken();
    return this.fetcher(`${this.baseUrl}${path}`, {
      credentials: "include",
      signal,
      headers: {
        Accept: "text/event-stream",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...clientHeaders(this.client),
      },
    });
  }

  private async resolveAccessToken(): Promise<string | null | undefined> {
    return typeof this.accessToken === "function"
      ? this.accessToken()
      : this.accessToken;
  }

  private async tryRecoverUnauthorized(): Promise<boolean> {
    if (!this.recoverUnauthorized) return false;
    try {
      return await this.recoverUnauthorized();
    } catch {
      return false;
    }
  }
}

async function cloudHttpError(response: Response): Promise<OrniTransportError> {
  let body: Record<string, unknown> = {};
  try {
    const value: unknown = await response.json();
    if (value && typeof value === "object" && !Array.isArray(value)) {
      body = value as Record<string, unknown>;
    }
  } catch {
    body = {};
  }
  const message = typeof body.message === "string"
    ? body.message
    : `Orni Cloud rejected the request (${response.status}).`;
  // Keep expired credentials distinct from a real capability denial.
  if (response.status === 401) return new OrniTransportError("UNAUTHENTICATED", message, true);
  if (response.status === 403) return new OrniTransportError("PERMISSION_DENIED", message);
  if (response.status === 404) return new OrniTransportError("NOT_FOUND", message);
  if (response.status === 426) return new OrniTransportError("UPGRADE_REQUIRED", message);
  if (response.status === 501) return new OrniTransportError("UNSUPPORTED", message);
  if (response.status === 409) return new OrniTransportError("CONFLICT", message);
  if (response.status === 400 || response.status === 422) {
    return new OrniTransportError("INVALID_ARGUMENT", message);
  }
  return new OrniTransportError("UNAVAILABLE", message, response.status >= 500);
}

function clientHeaders(client: CloudClientMetadata | undefined): Record<string, string> {
  if (!client) return {};
  if (!client.name || !client.version || !Number.isInteger(client.protocolVersion) || client.protocolVersion <= 0) {
    throw new OrniTransportError("INVALID_ARGUMENT", "Cloud client metadata is invalid.");
  }
  return {
    "X-Orni-Client": client.name,
    "X-Orni-Client-Version": client.version,
    "X-Orni-Protocol-Version": String(client.protocolVersion),
  };
}

function requestHeaders(
  supplied: HeadersInit | undefined,
  token: string | null | undefined,
  client: CloudClientMetadata | undefined,
  hasBody: boolean,
): Record<string, string> {
  const headers: Record<string, string> = {};
  new Headers(supplied).forEach((value, name) => {
    headers[name] = value;
  });
  replaceHeader(headers, "Accept", "application/json");
  if (hasBody && !hasHeader(headers, "Content-Type")) {
    replaceHeader(headers, "Content-Type", "application/json");
  }
  if (token) replaceHeader(headers, "Authorization", `Bearer ${token}`);
  for (const [name, value] of Object.entries(clientHeaders(client))) {
    replaceHeader(headers, name, value);
  }
  return headers;
}

function hasHeader(headers: Record<string, string>, name: string): boolean {
  return Object.keys(headers).some((candidate) => candidate.toLowerCase() === name.toLowerCase());
}

function replaceHeader(headers: Record<string, string>, name: string, value: string): void {
  for (const candidate of Object.keys(headers)) {
    if (candidate.toLowerCase() === name.toLowerCase()) delete headers[candidate];
  }
  headers[name] = value;
}
